import { useState } from 'react';
import { inventoryApi } from '../../api/services';
import type { InventoryCategoryDto, InventoryMovementLineViewDto, InventoryMovementListDto, SaveInventoryCategoryDto } from '../../api/types';
import {
  Alert, Badge, Button, Field, Form, Input, Loadable, Modal, PageHeader, Select, Table, Tabs, firstOfMonthIso, fmtDate, fmtMoney, fmtNumber, money, todayIso, useAction, useAsync,
} from '../../ui/components';
import { AccountPicker } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';
import { Link } from '../../router';
import { PERM, useCan } from '../../ui/access';

type TabKey = 'movements' | 'accounts';
const TYPES = [
  { value: 'VENTA', label: 'Venta (costo)' }, { value: 'DEVOLUCION_VENTA', label: 'Devolución de venta' }, { value: 'COMPRA', label: 'Entrada por compra' },
  { value: 'AJUSTE_ENTRADA', label: 'Ajuste de entrada' }, { value: 'AJUSTE_SALIDA', label: 'Ajuste de salida' }, { value: 'INICIAL', label: 'Inventario inicial' },
];

/** Contabilidad del inventario: los movimientos llegan del módulo de inventario; aquí se configuran las cuentas y se consultan. */
export function InventoryPage() {
  const [tab, setTab] = useState<TabKey>('movements');
  const categories = useAsync(() => inventoryApi.categories(), []);
  return (
    <>
      <PageHeader title="Inventario" subtitle="Costo de ventas y ajustes que envía el módulo de inventario" />
      <PageHelp page="inventory" />
      {categories.data && categories.data.length === 0 && (
        <Alert kind="warn">Todavía no hay cuentas configuradas: el inventario no puede enviar movimientos. <Button kind="link" onClick={() => setTab('accounts')}>Configurar cuentas</Button></Alert>
      )}
      <Tabs<TabKey> tabs={[{ key: 'movements', label: 'Movimientos' }, { key: 'accounts', label: 'Cuentas por categoría' }]} active={tab} onChange={setTab} />
      {tab === 'movements' && <MovementsTab />}
      {tab === 'accounts' && <AccountsTab state={categories} />}
    </>
  );
}

function MovementsTab() {
  const [f, setF] = useState({ dateFrom: firstOfMonthIso(), dateTo: todayIso(), type: '' });
  const list = useAsync(() => inventoryApi.movements({ dateFrom: f.dateFrom, dateTo: f.dateTo, type: f.type || undefined }), [f.dateFrom, f.dateTo, f.type]);
  const [viewing, setViewing] = useState<InventoryMovementListDto | null>(null);
  const { run, busy } = useAction();
  return (
    <>
      <div className="acc-toolbar">
        <Field label="Desde"><Input type="date" value={f.dateFrom} onChange={(v) => setF({ ...f, dateFrom: v })} /></Field>
        <Field label="Hasta"><Input type="date" value={f.dateTo} onChange={(v) => setF({ ...f, dateTo: v })} /></Field>
        <Field label="Tipo"><Select value={f.type} onChange={(v) => setF({ ...f, type: v })} placeholder="Todos" options={TYPES} /></Field>
        <Button busy={busy} onClick={() => run(() => inventoryApi.exportMovements({ dateFrom: f.dateFrom, dateTo: f.dateTo, type: f.type || undefined }))}>Exportar CSV</Button>
      </div>
      <Loadable state={list}>
        {(rows) => (
          <>
            <Table<InventoryMovementListDto> rows={rows} rowKey={(m) => m.id} empty="No hay movimientos en ese rango." columns={[
              { key: 'f', title: 'Fecha', render: (m) => fmtDate(m.date) },
              { key: 'i', title: 'Movimiento', render: (m) => <>{m.externalId}<div className="acc-muted">{[m.invoiceNumber ? `Factura ${m.invoiceNumber}` : null, m.reference].filter(Boolean).join(' · ')}</div></> },
              { key: 't', title: 'Tipo', render: (m) => <>{m.typeName}{m.reason && <div className="acc-muted">{m.reason}</div>}</> },
              { key: 'l', title: 'Productos', align: 'right', render: (m) => <Button kind="link" onClick={() => setViewing(m)}>{m.lines}</Button> },
              money<InventoryMovementListDto>('c', 'Costo', (m) => m.total),
              { key: 'a', title: 'Asiento', render: (m) => (m.entryCode ? <><Link to={`/entries/${m.entryCode}`}>{m.entryCode}</Link>{m.cancellationEntryCode && <div className="acc-muted">Reverso <Link to={`/entries/${m.cancellationEntryCode}`}>{m.cancellationEntryCode}</Link></div>}</> : <span className="acc-muted">Sin asiento</span>) },
              { key: 'e', title: 'Estado', render: (m) => (m.status === 'A' ? <Badge tone="ok">Vigente</Badge> : <><Badge tone="bad">Anulado</Badge>{m.cancellationReason && <div className="acc-muted">{m.cancellationReason}</div>}</>) },
            ]} />
            {rows.length > 0 && <p className="acc-muted">Costo total del rango (vigentes): {fmtMoney(rows.filter((m) => m.status === 'A').reduce((s, m) => s + m.total, 0))}</p>}
          </>
        )}
      </Loadable>
      {viewing && <LinesModal movement={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

function LinesModal({ movement, onClose }: { movement: InventoryMovementListDto; onClose: () => void }) {
  const lines = useAsync(() => inventoryApi.lines(movement.id), [movement.id]);
  return (
    <Modal title={`Productos de ${movement.externalId}`} onClose={onClose} size="lg">
      <Loadable state={lines}>
        {(rows) => (
          <Table<InventoryMovementLineViewDto> rows={rows} rowKey={(l) => `${l.productCode}-${l.category ?? ''}-${l.quantity}`} columns={[
            { key: 'p', title: 'Producto', render: (l) => <>{l.productName ?? l.productCode}<div className="acc-muted">{l.productCode}</div></> },
            { key: 'c', title: 'Categoría', render: (l) => l.category ?? <span className="acc-muted">Sin categoría</span> },
            { key: 'q', title: 'Cantidad', align: 'right', render: (l) => fmtNumber(l.quantity, 4) },
            { key: 'u', title: 'Costo unitario', align: 'right', render: (l) => fmtNumber(l.unitCost, 6) },
            money<InventoryMovementLineViewDto>('t', 'Costo', (l) => l.total),
            { key: 'a', title: 'Cuentas', render: (l) => <span className="acc-muted">{l.inventoryAccount}{l.counterpartAccount ? ` / ${l.counterpartAccount}` : ''}</span> },
          ]} />
        )}
      </Loadable>
      <div className="acc-actions"><Button onClick={onClose}>Cerrar</Button></div>
    </Modal>
  );
}

function AccountsTab({ state }: { state: ReturnType<typeof useAsync<InventoryCategoryDto[]>> }) {
  const canSetup = useCan(PERM.setup);
  const [editing, setEditing] = useState<InventoryCategoryDto | 'new' | null>(null);
  return (
    <>
      <Alert kind="info">Cada categoría del módulo de inventario se relaciona con sus cuentas. Escriba el nombre de la categoría exactamente como está en el inventario. La fila sin categoría ("Por defecto") se usa para los productos sin categoría y para cualquier categoría que no tenga fila propia.</Alert>
      {canSetup && <div className="acc-toolbar"><Button kind="primary" onClick={() => setEditing('new')}>Agregar categoría</Button></div>}
      <Loadable state={state}>
        {(rows) => (
          <Table<InventoryCategoryDto> rows={rows} rowKey={(c) => c.id} empty="Todavía no hay cuentas configuradas." columns={[
            { key: 'n', title: 'Categoría', render: (c) => (c.category ? c.category : <strong>Por defecto</strong>) },
            { key: 'i', title: 'Inventario', render: (c) => `${c.inventoryAccount} · ${c.inventoryAccountName ?? ''}` },
            { key: 'c', title: 'Costo de ventas', render: (c) => `${c.costAccount} · ${c.costAccountName ?? ''}` },
            { key: 'a', title: 'Ajustes', render: (c) => `${c.adjustmentAccount} · ${c.adjustmentAccountName ?? ''}` },
            { key: 'p', title: 'Compras por facturar', render: (c) => (c.purchaseClearingAccount ? `${c.purchaseClearingAccount} · ${c.purchaseClearingAccountName ?? ''}` : <span className="acc-muted">Compras por Cuentas por pagar</span>) },
            money<InventoryCategoryDto>('b', 'Saldo contable del inventario', (c) => c.inventoryAccountBalance),
            { key: 'e', title: 'Estado', render: (c) => <Badge tone={c.status === 'A' ? 'ok' : 'neutral'}>{c.status === 'A' ? 'Activa' : 'Inactiva'}</Badge> },
            { key: 'x', title: '', render: (c) => (canSetup ? <Button kind="link" onClick={() => setEditing(c)}>Editar…</Button> : null) },
          ]} />
        )}
      </Loadable>
      {editing && <CategoryModal category={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); state.reload(); }} />}
    </>
  );
}

function CategoryModal({ category, onClose, onSaved }: { category: InventoryCategoryDto | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<SaveInventoryCategoryDto>({
    category: category?.category ?? '', inventoryAccount: category?.inventoryAccount ?? '', costAccount: category?.costAccount ?? '',
    adjustmentAccount: category?.adjustmentAccount ?? '', purchaseClearingAccount: category?.purchaseClearingAccount ?? '', status: category?.status ?? 'A',
  });
  const { run, busy } = useAction();
  return (
    <Modal title={category ? 'Editar cuentas de la categoría' : 'Agregar categoría'} onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => {
        const dto = { ...f, category: (f.category ?? '').trim(), purchaseClearingAccount: f.purchaseClearingAccount || null };
        if (category) await inventoryApi.updateCategory(category.id, dto); else await inventoryApi.createCategory(dto);
        onSaved();
      }, { success: 'Cuentas guardadas.' })}>
        <div className="acc-grid-form">
          <Field label="Categoría del inventario" hint="Vacío = cuentas por defecto" wide><Input value={f.category ?? ''} onChange={(v) => setF({ ...f, category: v })} maxLength={100} /></Field>
          <Field label="Cuenta de inventario" hint="Activo, por ejemplo Inventario de productos"><AccountPicker value={f.inventoryAccount} onChange={(v) => setF({ ...f, inventoryAccount: v })} /></Field>
          <Field label="Cuenta de costo de ventas" hint="Costo de lo vendido"><AccountPicker value={f.costAccount} onChange={(v) => setF({ ...f, costAccount: v })} /></Field>
          <Field label="Cuenta de ajustes de inventario" hint="Mermas, vencimientos y diferencias de conteo"><AccountPicker value={f.adjustmentAccount} onChange={(v) => setF({ ...f, adjustmentAccount: v })} /></Field>
          <Field label="Cuenta de compras por facturar" hint="Opcional. Vacía = las compras se contabilizan con la factura del proveedor"><AccountPicker value={f.purchaseClearingAccount ?? ''} onChange={(v) => setF({ ...f, purchaseClearingAccount: v })} /></Field>
          <Field label="Estado"><Select value={f.status} onChange={(v) => setF({ ...f, status: v })} options={[{ value: 'A', label: 'Activa' }, { value: 'I', label: 'Inactiva' }]} /></Field>
        </div>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!f.inventoryAccount || !f.costAccount || !f.adjustmentAccount}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}
